import * as THREE from "../../libs/three.js/build/three.module.js";
import { EventDispatcher } from "../EventDispatcher.js";

export class CloudTransformTool extends EventDispatcher {
  constructor(viewer) {
    super();
    this.viewer = viewer;

    // Stan bazowy
    this.basePosition = new THREE.Vector3();
    this.baseRotation = new THREE.Euler();

    // Transformacje (a–f)
    this.transformState = { a: 0, b: 0, c: 0, d: 0, e: 0, f: 0 };

    // Parametry domyślne
    this.positionRange = 150;
    this.rotationRange = Math.PI;
    this.defaultStep = 0.1;

    // Docelowa chmura
    this.targetPointCloud = null;

    // Tworzymy panel w sidebarze
    this.createTransformPanel();

    this.bindEvents();
  }

  /** Ustaw docelową chmurę do transformacji */
  setTargetPointCloud(pointcloud) {
    this.targetPointCloud = pointcloud;
    if (pointcloud) {
      this.basePosition.copy(pointcloud.position);
      this.baseRotation.copy(pointcloud.rotation);
      this.updateGeneratedCode();
    }
  }

  /** Aktualizacja kodu oraz pozycji chmury */
  updateGeneratedCode() {
    const { a, b, c, d, e, f } = this.transformState;
    const code = `
pointcloud.position.set(pointcloud.position.x + ${a}, pointcloud.position.y + ${b}, pointcloud.position.z + ${c});
pointcloud.rotation.set(pointcloud.rotation.x + ${d}, pointcloud.rotation.y + ${e}, pointcloud.rotation.z + ${f});
    `;
    this.codeOutput.textContent = code;

    if (this.targetPointCloud) {
      this.targetPointCloud.position.set(
        this.basePosition.x + a,
        this.basePosition.y + b,
        this.basePosition.z + c
      );
      this.targetPointCloud.rotation.set(
        this.baseRotation.x + d,
        this.baseRotation.y + e,
        this.baseRotation.z + f
      );
    }
  }

  /** Tworzy jeden suwak + input */
  createControl(idPrefix, min, max, step, onChange) {
    const wrapper = document.createElement("div");
    wrapper.style.display = "flex";
    wrapper.style.marginBottom = "5px";
    wrapper.style.alignItems = "center";

    const number = document.createElement("input");
    number.type = "number";
    number.step = step;
    number.min = min;
    number.max = max;
    number.value = "0";
    number.style.width = "90px";
    number.id = idPrefix + "_num";

    const range = document.createElement("input");
    range.type = "range";
    range.min = min;
    range.max = max;
    range.step = step;
    range.value = "0";
    range.style.flex = "1";
    range.style.marginLeft = "10px";
    range.id = idPrefix + "_range";

    number.addEventListener("input", () => {
      range.value = number.value;
      onChange(parseFloat(number.value));
      this.updateGeneratedCode();
    });
    range.addEventListener("input", () => {
      number.value = range.value;
      onChange(parseFloat(range.value));
      this.updateGeneratedCode();
    });

    wrapper.appendChild(number);
    wrapper.appendChild(range);
    return wrapper;
  }

  /** Tworzy panel sterujący */
  createTransformPanel() {
    // Kontener główny
    this.panel = document.createElement("div");
    this.panel.style.display = "none";
    this.cloudSelector = document.createElement("select");
    this.cloudSelector.id = "cloudSelector";
    this.cloudSelector.style.width = "100%";
    this.cloudSelector.style.marginBottom = "10px";
    this.cloudSelector.style.marginTop = "10px";
    this.panel.append(this.cloudSelector);
    this.panel.id = "cloud-transform-panel";
    this.panel.style.zIndex = "100000";
    this.panel.style.position = "relative";
    this.panel.style.background = "rgba(255,255,255,0.95)";
    this.panel.style.padding = "10px";
    this.panel.style.borderRadius = "5px";
    this.panel.style.fontFamily = "sans-serif";
    this.panel.style.fontSize = "13px";
    this.panel.style.maxWidth = "400px";

    // wypełnij listę:
    this.refreshCloudList();
    this.cloudSelector.addEventListener("change", () => {
      const name = this.cloudSelector.value;
      const cloud = this.viewer.scene.pointclouds.find(pc => pc.name === name);
      if (cloud) this.setTargetPointCloud(cloud);
    });

    // Select kroku
    const stepDiv = document.createElement("div");
    stepDiv.innerHTML = `<b>Krok</b> `;
    this.stepSelector = document.createElement("select");
    [0.01, 0.05, 0.1, 0.5, 1].forEach((val) => {
      const opt = document.createElement("option");
      opt.value = val;
      opt.textContent = val;
      if (val === 0.1) opt.selected = true;
      this.stepSelector.appendChild(opt);
    });
    stepDiv.appendChild(this.stepSelector);
    this.panel.appendChild(stepDiv);

    this.panel.appendChild(document.createElement("hr"));

    // Pozycja
    this.panel.appendChild(document.createTextNode("Pozycja"));
    this.posControls = document.createElement("div");
    this.panel.appendChild(this.posControls);

    this.panel.appendChild(document.createElement("hr"));

    // Rotacja
    this.panel.appendChild(document.createTextNode("Rotacja"));
    this.rotControls = document.createElement("div");
    this.panel.appendChild(this.rotControls);

    this.panel.appendChild(document.createElement("hr"));

    // Kod
    this.panel.appendChild(document.createTextNode("Generowany kod:"));
    this.codeOutput = document.createElement("pre");
    this.codeOutput.style.fontFamily = "monospace";
    this.codeOutput.style.whiteSpace = "pre-wrap";
    this.panel.appendChild(this.codeOutput);

    // Podpinamy do sidebaru (jeśli istnieje)
    this.domRoot = this.viewer.renderer.domElement.parentElement;
    this.domRoot.appendChild(this.panel);

    // Tworzymy kontrolki
    this.createTransformUI();

    // Obsługa zmiany kroku
    this.stepSelector.addEventListener("change", (e) => {
      const step = parseFloat(e.target.value);
      ["a", "b", "c", "d", "e", "f"].forEach((id) => {
        document.getElementById(id + "_num").step = step;
        document.getElementById(id + "_range").step = step;
      });
    });
  }

  /** Tworzy suwaki dla pozycji i rotacji */
  createTransformUI() {
    const addCtrl = (id, min, max, step, setter) => {
      const control = this.createControl(id, min, max, step, setter);
      return control;
    };

    this.posControls.appendChild(
      addCtrl("a", -this.positionRange, this.positionRange, this.defaultStep, (v) => (this.transformState.a = v))
    );
    this.posControls.appendChild(
      addCtrl("b", -this.positionRange, this.positionRange, this.defaultStep, (v) => (this.transformState.b = v))
    );
    this.posControls.appendChild(
      addCtrl("c", -this.positionRange, this.positionRange, this.defaultStep, (v) => (this.transformState.c = v))
    );

    this.rotControls.appendChild(
      addCtrl("d", -this.rotationRange, this.rotationRange, this.defaultStep, (v) => (this.transformState.d = v))
    );
    this.rotControls.appendChild(
      addCtrl("e", -this.rotationRange, this.rotationRange, this.defaultStep, (v) => (this.transformState.e = v))
    );
    this.rotControls.appendChild(
      addCtrl("f", -this.rotationRange, this.rotationRange, this.defaultStep, (v) => (this.transformState.f = v))
    );
    this.updateGeneratedCode();
  }

  refreshCloudList() {
    this.cloudSelector.innerHTML = "";
    const clouds = this.viewer.scene.pointclouds;

    if (clouds.length === 0) {
      const opt = document.createElement("option");
      opt.value = "";
      opt.textContent = "Brak chmur";
      this.cloudSelector.appendChild(opt);
      return;
    }

    clouds.forEach(pc => {
      const opt = document.createElement("option");
      opt.value = pc.name;
      opt.textContent = pc.name;
      this.cloudSelector.appendChild(opt);
    });

    // ustaw pierwszą chmurę jako target
    this.setTargetPointCloud(clouds[0]);
    this.cloudSelector.value = clouds[0].name;
  }

  bindEvents() {
    this.viewer.scene.addEventListener('pointcloud_added', () => {
      this.refreshCloudList();
    });
  }

}
